import { ApiError } from './errors/ApiError';
import type { AIPrompt, AiRequest } from './TeoApi';
import type { TeoCore } from './TeoCore';
import { TeoText, type AnalysisConfiguration } from './text/TeoText';
import { TeoEventTarget } from './utility/TeoEventTarget';

type PromptMap = Record<string, AIPrompt[]>;

type TeoCreaterEvents = {
  textCreatedFromAiSuggestion: (details: TeoText) => void;
};

export class TextCreator extends TeoEventTarget<TeoCreaterEvents> {
  private core: TeoCore;
  private prompts: PromptMap = {};
  private initialized = false;

  constructor(core: TeoCore) {
    super();
    this.core = core;
    if (this.core.user.isLoggedIn) {
      this.init();
    }
    this.core.user.events.addEventListener('logIn', () => this.init());
  }

  async init() {
    await Promise.all([
      this.loadPrompts('plain_language_translation'),
      this.loadPrompts('create_text'),
    ]);
    this.initialized = true;
  }

  async loadPrompts(type: string) {
    console.log(`Loading AI prompts for type "${type}"...`);
    const prompts = await this.core.api.getAiPrompts(type);
    if (!ApiError.matches(prompts)) {
      this.prompts[type] = prompts;
    } else {
      this.prompts[type] = [];
      console.error(`Failed to load AI prompts for type "${type}":`, prompts.message);
    }
  }

  getPrompts(type: string): AIPrompt[] {
    return this.prompts[type] ?? [];
  }

  /**
   * Creates a new text using an AI suggestion.
   * @throws Error if prompts are not loaded yet.
   */
  async createTextFromAiSuggestion(
    sourceText: string,
    promptType: string,
    // promptId: number,
    config?: Partial<AnalysisConfiguration>,
  ): Promise<TeoText> {
    const suggestion = await this.getAiSuggestion(sourceText, promptType, config);

    const newText = this.core.createTemporaryText(suggestion);
    if (suggestion.trim().length > 0) {
      this.triggerEvent('textCreatedFromAiSuggestion', newText);
    }
    return newText;
  }

  /**
   * Gets an AI suggestion from the API
   * @throws Error if prompts are not loaded yet.
   */
  async getAiSuggestion(
    sourceText: string,
    promptType: string,
    // promptId: number,
    config?: Partial<AnalysisConfiguration>,
  ): Promise<string> {
    if (!this.initialized) {
      throw new Error('TextCreator prompts not loaded. May have to wait for initialization.');
    }
    // const prompt = this.getPrompts(promptType).find((p) => p.id === promptId);
    const prompt = this.getPrompts(promptType)[0];
    if (!prompt) {
      console.error(`No prompt found for type "${promptType}".`);
      return '';
    }

    const aiRequest: AiRequest = {
      context: {
        text: sourceText,
        textAfter: '',
        textBefore: '',
        textFocus: '',
        replacementWrapper: 0,
      },
      tool: prompt.type,
      tool_id: prompt.id,
      config: {
        ...(config ?? this.core.activeText.configuration),
        evaluation_profile: this.core.staticConfig.evaluationProfile,
      } as AnalysisConfiguration & { evaluation_profile: number },
    };

    const suggestion = await this.core.api.getSimpleAiSuggestion(aiRequest);
    if (ApiError.matches(suggestion) || !suggestion.suggestions?.[0]) {
      console.error('Failed to get AI suggestion:', suggestion);
      return '';
    }
    return suggestion.suggestions[0];
  }
}
